import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../../contexts/LanguageContext';
import { FaArrowLeft, FaEnvelope, FaPaperPlane } from 'react-icons/fa';

const Container = styled.div`
  padding: 1rem;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  color: var(--text-primary);
  font-size: 16px;
  cursor: pointer;
  padding: 0.5rem 0;
  margin-bottom: 1rem;

  svg {
    font-size: 20px;
  }
`;

const Title = styled.h1`
  font-size: 24px;
  color: var(--text-primary);
  margin: 0 0 1.5rem 0;
`;

const Card = styled.div`
  background-color: var(--bg-secondary);
  border-radius: 10px;
  padding: 2rem;
  margin-bottom: 1.5rem;
`;

const EmailAddress = styled.a`
  display: flex;
  align-items: center;
  gap: 1rem;
  color: var(--primary-color);
  font-size: 20px;
  text-decoration: none;
  margin-bottom: 1.5rem;

  &:hover {
    color: var(--primary-color-dark);
  }

  svg {
    font-size: 24px;
  }
`;

const Description = styled.p`
  color: var(--text-secondary);
  line-height: 1.6;
  margin: 0 0 1.5rem 0;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  color: var(--text-primary);
  font-size: 14px;
  font-weight: 500;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 5px;
  background-color: var(--bg-primary);
  color: var(--text-primary);
  font-size: 16px;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }
`;

const TextArea = styled.textarea`
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 5px;
  background-color: var(--bg-primary);
  color: var(--text-primary);
  font-size: 16px;
  min-height: 150px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }
`;

const SubmitButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  background-color: var(--primary-color);
  color: white;
  border: none;
  border-radius: 5px;
  padding: 1rem;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: var(--primary-color-dark);
  }

  &:disabled {
    background-color: var(--bg-secondary);
    color: var(--text-secondary);
    cursor: not-allowed;
  }

  svg {
    font-size: 18px;
  }
`;

const Email: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // TODO: Implement email sending logic
    console.log('Sending email:', { subject, message });
    navigate('/profile/help');
  };

  return (
    <Container>
      <BackButton onClick={() => navigate('/profile/help')}>
        <FaArrowLeft />
        {t('common.back')}
      </BackButton>

      <Title>{t('help.email.title')}</Title>

      <Card>
        <EmailAddress href="mailto:support@taximoove.com">
          <FaEnvelope />
          support@taximoove.com
        </EmailAddress>

        <Description>{t('help.email.description')}</Description>

        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label>{t('help.email.subject')}</Label>
            <Input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={t('help.email.subjectPlaceholder')}
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>{t('help.email.message')}</Label>
            <TextArea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={t('help.email.messagePlaceholder')}
              required
            />
          </FormGroup>

          <SubmitButton type="submit" disabled={!subject.trim() || !message.trim()}>
            <FaPaperPlane />
            {t('help.email.send')}
          </SubmitButton>
        </Form>
      </Card>
    </Container>
  );
};

export default Email; 